import React from "react";
import { Box, Typography } from "@mui/material";
import { FormatFullName } from "app/utils/helper";
import moment from "moment";

const ClassicCertificate = ({
  student,
  schoolDetail,
  selectedSession,
  checkBoxEnabled = {},
  certificateTitle = "Certificate of Achievement",
  certificateSubtitle = "This is to certify that",
  courseName = "Academic Excellence",
  description = "Has successfully completed the academic requirements",
  primaryColor = "#1e3a8a",
  secondaryColor = "#d4af37",
  accentColor = "#991b1b",
  fontFamily = "Georgia",
  borderStyle = "double",
  borderWidth = "8",
  pageOrientation = "landscape",
  fieldStyles = {},
}) => {
  const studentFullName = FormatFullName(
    student?.firstName,
    student?.middleName,
    student?.lastName
  );

  const currentDate = moment().format("DD MMMM, YYYY");

  // Calculate dimensions based on orientation
  const isLandscape = pageOrientation === "landscape";

  return (
    <Box
      sx={{
        width: isLandscape ? "297mm" : "210mm",
        height: isLandscape ? "210mm" : "297mm",
        margin: "0 auto",
        padding: "0",
        fontFamily: fontFamily || "Georgia",
        backgroundColor: "#ffffff",
        boxSizing: "border-box",
        position: "relative",
      }}
    >
      <Box
        sx={{
          border: `${borderWidth}px ${borderStyle} ${primaryColor}`,
          padding: "10px",
          backgroundColor: "#ffffff",
          height: "100%",
          width: "100%",
          boxSizing: "border-box",
          position: "absolute",
          top: 0,
          left: 0,
        }}
      >
        <Box
          sx={{
            border: `2px solid ${secondaryColor}`,
            padding: isLandscape ? "20px 40px" : "30px 25px",
            position: "relative",
            height: "100%",
            width: "100%",
            display: "flex",
            flexDirection: "column",
            boxSizing: "border-box",
            justifyContent: "space-between",
          }}
        >
          <Box
            sx={{
              position: "relative",
              zIndex: 1,
              flex: 1,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Header Section */}
            <Box
              sx={{
                textAlign: "center",
                marginBottom: isLandscape ? "15px" : "35px",
              }}
            >
              {checkBoxEnabled.schoolLogo && (
                <Box
                  sx={{
                    marginBottom: "15px",
                  }}
                >
                  <img
                    src={schoolDetail?.schoolLogo}
                    alt="School Logo"
                    style={{
                      height: isLandscape ? "80px" : "100px",
                      width: isLandscape ? "80px" : "100px",
                      objectFit: "contain",
                    }}
                  />
                </Box>
              )}

              {checkBoxEnabled.schoolName && (
                <Typography
                  sx={{
                    fontSize: isLandscape ? "32px" : "38px",
                    fontWeight: 700,
                    color: primaryColor,
                    fontFamily: fontFamily,
                    marginBottom: "5px",
                    letterSpacing: "1px",
                  }}
                >
                  {schoolDetail?.schoolName || "School Name"}
                </Typography>
              )}

              {checkBoxEnabled.schoolAddress && (
                <Typography
                  sx={{
                    fontSize: isLandscape ? "14px" : "16px",
                    color: "#4b5563",
                    fontFamily: fontFamily,
                  }}
                >
                  {schoolDetail?.schoolAddress?.address ?? "School Address"}
                </Typography>
              )}
            </Box>

            {/* Certificate Title */}
            {checkBoxEnabled.certificateTitle && (
              <Typography
                sx={{
                  fontSize: `${
                    fieldStyles.certificateTitle?.fontSize ||
                    (isLandscape ? 48 : 56)
                  }px`,
                  fontWeight: fieldStyles.certificateTitle?.fontWeight || 700,
                  color: fieldStyles.certificateTitle?.color || secondaryColor,
                  fontFamily:
                    fieldStyles.certificateTitle?.fontFamily || fontFamily,
                  fontStyle:
                    fieldStyles.certificateTitle?.fontStyle || "normal",
                  textDecoration:
                    fieldStyles.certificateTitle?.textDecoration || "none",
                  textAlign: "center",
                  marginTop: isLandscape ? "20px" : "30px",
                  marginBottom: isLandscape ? "18px" : "25px",
                  textTransform: "uppercase",
                  letterSpacing: "2px",
                }}
              >
                {certificateTitle}
              </Typography>
            )}

            {/* Decorative Line */}
            <Box
              sx={{
                width: isLandscape ? "120px" : "150px",
                height: "4px",
                backgroundColor: secondaryColor,
                margin: `0 auto ${isLandscape ? "18px" : "25px"}`,
              }}
            />

            {/* Subtitle */}
            {checkBoxEnabled.certificateSubtitle && (
              <Typography
                sx={{
                  fontSize: `${
                    fieldStyles.certificateSubtitle?.fontSize ||
                    (isLandscape ? 20 : 24)
                  }px`,
                  fontWeight:
                    fieldStyles.certificateSubtitle?.fontWeight || "normal",
                  color: fieldStyles.certificateSubtitle?.color || "#374151",
                  fontFamily:
                    fieldStyles.certificateSubtitle?.fontFamily || fontFamily,
                  fontStyle:
                    fieldStyles.certificateSubtitle?.fontStyle || "italic",
                  textDecoration:
                    fieldStyles.certificateSubtitle?.textDecoration || "none",
                  textAlign: "center",
                  marginBottom: isLandscape ? "18px" : "25px",
                }}
              >
                {certificateSubtitle}
              </Typography>
            )}

            {/* Student Name */}
            {checkBoxEnabled.studentName && (
              <Typography
                sx={{
                  fontSize: `${
                    fieldStyles.studentName?.fontSize || (isLandscape ? 40 : 48)
                  }px`,
                  fontWeight: fieldStyles.studentName?.fontWeight || 700,
                  color: fieldStyles.studentName?.color || primaryColor,
                  fontFamily: fieldStyles.studentName?.fontFamily || fontFamily,
                  fontStyle: fieldStyles.studentName?.fontStyle || "normal",
                  textDecoration:
                    fieldStyles.studentName?.textDecoration || "none",
                  textAlign: "center",
                  marginBottom: isLandscape ? "15px" : "20px",
                  borderBottom: `3px solid ${
                    fieldStyles.studentName?.color || primaryColor
                  }`,
                  display: "block",
                  padding: isLandscape ? "8px 40px" : "10px 50px",
                  margin: `0 auto ${isLandscape ? "15px" : "20px"}`,
                  width: "fit-content",
                }}
              >
                {studentFullName}
              </Typography>
            )}

            {/* Grade/Class Info */}
            {checkBoxEnabled.class && (
              <Typography
                sx={{
                  fontSize: isLandscape ? "16px" : "18px",
                  color: "#6b7280",
                  fontFamily: fontFamily,
                  textAlign: "center",
                  marginBottom: isLandscape ? "15px" : "20px",
                }}
              >
                Class: {student?.className || "N/A"} -{" "}
                {student?.section || "N/A"}
              </Typography>
            )}

            {/* Course/Achievement Name */}
            {checkBoxEnabled.courseName && (
              <Typography
                sx={{
                  fontSize: `${
                    fieldStyles.courseName?.fontSize || (isLandscape ? 26 : 32)
                  }px`,
                  fontWeight: fieldStyles.courseName?.fontWeight || 600,
                  color: fieldStyles.courseName?.color || accentColor,
                  fontFamily: fieldStyles.courseName?.fontFamily || fontFamily,
                  fontStyle: fieldStyles.courseName?.fontStyle || "normal",
                  textDecoration:
                    fieldStyles.courseName?.textDecoration || "none",
                  textAlign: "center",
                  marginBottom: isLandscape ? "18px" : "25px",
                }}
              >
                {courseName}
              </Typography>
            )}

            {/* Description */}
            {checkBoxEnabled.description && (
              <Typography
                sx={{
                  fontSize: `${
                    fieldStyles.description?.fontSize || (isLandscape ? 18 : 22)
                  }px`,
                  fontWeight: fieldStyles.description?.fontWeight || "normal",
                  color: fieldStyles.description?.color || "#4b5563",
                  fontFamily: fieldStyles.description?.fontFamily || fontFamily,
                  fontStyle: fieldStyles.description?.fontStyle || "normal",
                  textDecoration:
                    fieldStyles.description?.textDecoration || "none",
                  textAlign: "center",
                  marginBottom: isLandscape ? "20px" : "30px",
                  lineHeight: 1.6,
                  maxWidth: isLandscape ? "90%" : "95%",
                  margin: `0 auto ${isLandscape ? "20px" : "30px"}`,
                }}
              >
                {description}
              </Typography>
            )}

            {/* Completion Date */}
            {checkBoxEnabled.academicSession && (
              <Typography
                sx={{
                  fontSize: isLandscape ? "16px" : "18px",
                  color: "#374151",
                  fontFamily: fontFamily,
                  textAlign: "center",
                  marginBottom: isLandscape ? "25px" : "35px",
                  fontWeight: 500,
                }}
              >
                Academic Session: {selectedSession}
              </Typography>
            )}

            {/* Footer Section with Signatures */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-end",
                marginTop: "auto",
                paddingTop: isLandscape ? "25px" : "35px",
              }}
            >
              <Box
                sx={{
                  textAlign: "center",
                  minWidth: isLandscape ? "140px" : "160px",
                }}
              >
                {checkBoxEnabled.principalSignature && (
                  <>
                    <Box
                      sx={{
                        width: isLandscape ? "140px" : "160px",
                        height: "2px",
                        backgroundColor: "#000",
                        marginBottom: "10px",
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: isLandscape ? "14px" : "16px",
                        color: "#6b7280",
                        fontFamily: fontFamily,
                      }}
                    >
                      Principal
                    </Typography>
                  </>
                )}
              </Box>

              {checkBoxEnabled.seal && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Box
                    sx={{
                      width: isLandscape ? "80px" : "95px",
                      height: isLandscape ? "80px" : "95px",
                      borderRadius: "50%",
                      border: `4px double ${primaryColor}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: `${secondaryColor}15`,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: isLandscape ? "12px" : "14px",
                        fontWeight: 600,
                        textAlign: "center",
                      }}
                    ></Typography>
                  </Box>
                </Box>
              )}

              <Box
                sx={{
                  textAlign: "center",
                  minWidth: isLandscape ? "140px" : "160px",
                }}
              >
                {checkBoxEnabled.coordinatorSignature && (
                  <>
                    <Box
                      sx={{
                        width: isLandscape ? "140px" : "160px",
                        height: "2px",
                        backgroundColor: "#000",
                        marginBottom: "10px",
                      }}
                    />
                    {/*
                    <Typography
                      sx={{
                        fontSize: `${fieldStyles.coordinatorName?.fontSize || 16}px`,
                        fontWeight: fieldStyles.coordinatorName?.fontWeight || 600,
                        color: fieldStyles.coordinatorName?.color || "#1f2937",
                        fontFamily: fieldStyles.coordinatorName?.fontFamily || fontFamily,
                        fontStyle: fieldStyles.coordinatorName?.fontStyle || "normal",
                        textDecoration: fieldStyles.coordinatorName?.textDecoration || "none",
                        marginBottom: "4px",
                        
                      }}
                    >
                      {coordinatorName}
                    </Typography> */}
                    <Typography
                      sx={{
                        fontSize: isLandscape ? "14px" : "16px",
                        color: "#6b7280",
                        fontFamily: fontFamily,
                      }}
                    >
                      Academic Coordinator
                    </Typography>
                  </>
                )}
              </Box>
            </Box>

            {/* Date */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: isLandscape ? "20px" : "25px",
                paddingTop: isLandscape ? "15px" : "20px",
                borderTop: "2px solid #e5e7eb",
              }}
            >
              {checkBoxEnabled.date && (
                <Typography
                  sx={{
                    fontSize: isLandscape ? "12px" : "14px",
                    color: "#6b7280",
                    fontFamily: fontFamily,
                  }}
                >
                  Date: {currentDate}
                </Typography>
              )}
            </Box>

            {/* Decorative Corners */}
            {/* {["top-left", "top-right", "bottom-left", "bottom-right"].map((position) => {
              const positions = {
                "top-left": { top: "10px", left: "10px" },
                "top-right": { top: "10px", right: "10px" },
                "bottom-left": { bottom: "10px", left: "10px" },
                "bottom-right": { bottom: "10px", right: "10px" },
              };

              return (
                <Box
                  key={position}
                  sx={{
                    position: "absolute",
                    width: "40px",
                    height: "40px",
                    ...positions[position],
                    "&::before, &::after": {
                      content: '""',
                      position: "absolute",
                      backgroundColor: secondaryColor,
                    },
                    "&::before": {
                      width: "100%",
                      height: "3px",
                      top: position.includes("top") ? 0 : "auto",
                      bottom: position.includes("bottom") ? 0 : "auto",
                    },
                    "&::after": {
                      width: "3px",
                      height: "100%",
                      left: position.includes("left") ? 0 : "auto",
                      right: position.includes("right") ? 0 : "auto",
                    },
                    
                  }}
                />
              );
            })} */}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ClassicCertificate;
